import { Box, Checkbox, Chip, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { StructureSetInfo } from '~/components/InfoTooltip/StructureSetInfo';
import { getModalityIcon, Modality } from '~/domain/dicom/modality';
import { DoseMatrix } from '~/domain/patient/doseMatrix';
import { Image3D } from '~/domain/patient/image3d';
import { StructureSet } from '~/domain/patient/structureset';
import { ActiveItem } from '../../useActiveItems';
import { SelectedDatasets } from '../LoadedPatientDataTree';
import DoseLeaf from './DoseLeaf';

interface StructureSetLeafProps {
    structureSet: StructureSet;
    doses: DoseMatrix[];
    image: Image3D;
    selectedDatasets: SelectedDatasets;
    activeItems: ActiveItem[];
    onSelect: React.Dispatch<React.SetStateAction<SelectedDatasets>>;
}

const StructureSetLeaf = ({
    structureSet,
    doses,
    image,
    selectedDatasets,
    activeItems,
    onSelect,
}: StructureSetLeafProps) => {
    const isStructureSetActive = activeItems.some(
        (item) => item.dicomUid === structureSet.dicomUid
    );
    const isStructureSetSelected =
        selectedDatasets?.structureSet?.dicomUid === structureSet.dicomUid;

    const handleSelect = () => {
        if (isStructureSetSelected) {
            onSelect((prev) => ({
                ...prev,
                structureSet: undefined,
                dose: undefined,
            }));
        } else {
            onSelect((state) => ({
                image,
                structureSet,
                registration: state.registration,
            }));
        }
    };

    return (
        <Box key={structureSet.dicomUid} display="grid">
            <Box
                display="flex"
                justifyContent="space-between"
                py={0.5}
                pr={1}
                pl={3}
                onClick={handleSelect}
                sx={(theme) => ({
                    '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                    },
                    ...(isStructureSetActive && {
                        backgroundColor: 'rgba(0, 165, 185, 0.05)',
                    }),
                })}
            >
                <Box
                    display="grid"
                    alignItems="center"
                    gridTemplateColumns="auto auto auto auto auto 1fr auto"
                    gap={0.5}
                    width="100%"
                >
                    <Checkbox
                        disableRipple
                        size="small"
                        color="primary"
                        checked={isStructureSetSelected}
                        sx={{
                            '&': {
                                padding: 0,
                            },
                        }}
                    />

                    <Box>{getModalityIcon(Modality.RTSTRUCT, 12)}</Box>
                    <Typography display="flex" alignItems="center">
                        {Modality.RTSTRUCT}
                    </Typography>
                    <Typography
                        display="flex"
                        alignItems="center"
                        variant="body2"
                        sx={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {structureSet.structureSetLabel}
                    </Typography>

                    <Typography
                        variant="body2"
                        sx={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {structureSet.structureSetDescription
                            ? `- ${structureSet.structureSetDescription}`
                            : ''}
                    </Typography>
                    <Typography color="primary">
                        <StructureSetInfo structureSet={structureSet} />
                    </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                    {isStructureSetActive && (
                        <Chip
                            variant="outlined"
                            size="small"
                            sx={{
                                fontSize: 10,
                                height: 20,
                                padding: 0.5,
                                lineHeight: 0.9,
                            }}
                            color="success"
                            label={<FormattedMessage id="active" />}
                        />
                    )}
                </Box>
            </Box>

            {doses.map((dose) => (
                <DoseLeaf
                    key={dose.dicomUid}
                    dose={dose}
                    image={image}
                    structureSet={structureSet}
                    selectedDatasets={selectedDatasets}
                    activeItems={activeItems}
                    onSelect={onSelect}
                />
            ))}
        </Box>
    );
};

export default StructureSetLeaf;
